import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';
import type { AdminUser } from '../lib/api';

const TOKEN_KEY = 'pd_token';
const USER_KEY = 'pd_admin_user';

interface AuthCtx {
  token: string | null;
  user: AdminUser | null;
  login: (token: string, user: AdminUser) => void;
  logout: () => void;
  updateUser: (patch: Partial<AdminUser>) => void;
}

const AuthContext = createContext<AuthCtx | null>(null);

function readStoredUser(): AdminUser | null {
  try {
    const raw = localStorage.getItem(USER_KEY);
    return raw ? (JSON.parse(raw) as AdminUser) : null;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [token, setToken] = useState<string | null>(() => localStorage.getItem(TOKEN_KEY));
  const [user, setUser] = useState<AdminUser | null>(() => readStoredUser());

  const login = useCallback((newToken: string, newUser: AdminUser) => {
    localStorage.setItem(TOKEN_KEY, newToken);
    localStorage.setItem(USER_KEY, JSON.stringify(newUser));
    setToken(newToken);
    setUser(newUser);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    setToken(null);
    setUser(null);
  }, []);

  const updateUser = useCallback((patch: Partial<AdminUser>) => {
    setUser((prev) => {
      // No usar `if (!prev) return prev` — si el contexto llegara a tener
      // user=null con un token todavía válido (sesión vieja, storage parcial),
      // eso descartaría en silencio cada actualización para siempre, incluida
      // la que repuebla el usuario al montar /perfil.
      const next = { ...prev, ...patch } as AdminUser;
      localStorage.setItem(USER_KEY, JSON.stringify(next));
      return next;
    });
  }, []);

  return (
    <AuthContext.Provider value={{ token, user, login, logout, updateUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
